import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import reactStringReplace from 'react-string-replace';
import { Box, Dialog, IconButton, ListItemText, Paper, Slide, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import clsx from 'clsx';
import { ISearchCategory, ISearchGroup, useMenuSearch } from 'src/hooks/useMenuSearch';
import { useResponsive } from 'src/hooks/useResponsive';
import { useSuggestionsContext } from 'src/hooks/useSuggestionsContext';
import { SearchInput } from 'lib/SearchInput';
import { isDefined, isEmptyString } from 'lib/typeInference';
import { useLocalHistory } from 'lib/useLocalHistory';
import { ApplicationState } from 'store/store';
import { IEnrichedProduct, IProduct, ProductGroup } from './model/Menu';
import { useAddModal } from './suggestions/useAddModal';
import { MenuProductInfo } from './MenuProductInfo';
import { MenuViewBasketButton } from './MenuViewBasketButton';
import { MenuSearchCategory } from './MenuSearchCategory';
import { useQuery } from 'src/hooks/useQuery';
import { useLocation } from 'react-router';
import { TransitionProps } from '@mui/material/transitions';

const PREFIX = 'MenuSearch';

const classes = {
    titleGroup: `${PREFIX}titleGroup`,
    titleCategory: `${PREFIX}titleCategory`,
    center: `${PREFIX}center`,
    paper: `${PREFIX}paper`,
    hint: `${PREFIX}hint`,
    appBar: `${PREFIX}appBar`,
    backButton: `${PREFIX}backButton`,
    input: `${PREFIX}input`,
    productsListBox: `${PREFIX}productsListBox`,
    messageBox: `${PREFIX}messageBox`
};

const StyledBox = styled(Box)(({ theme }) => ({
    [`& .${classes.titleGroup}`]: {
        marginBottom: theme.spacing(2),
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.titleTextColour
    },

    [`& .${classes.titleCategory}`]: {
        marginBottom: theme.spacing(1),
        fontWeight: theme.typography.fontWeightBold,
        color: theme.palette.titleTextColour
    },

    [`& .${classes.center}`]: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
    },

    [`& .${classes.paper}`]: {
        flexDirection: 'column',
        height: '100%'
    },

    [`& .${classes.hint}`]: {
        marginBottom: theme.spacing(1),
        color: theme.palette.text.primary
    },

    [`& .${classes.appBar}`]: {
        position: 'fixed',
        top: 0,
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        padding: theme.spacing(1),
        backgroundColor: theme.palette.background.default,
        zIndex: 1
    },

    [`& .${classes.backButton}`]: {
        color: theme.palette.primary.main
    },

    [`& .${classes.input}`]: {
        maxWidth: '300px',
        width: '100%'
    },

    [`& .${classes.productsListBox}`]: {
        padding: theme.spacing(1.5, 1.5, 7, 1.5)
    },

    [`& .${classes.messageBox}`]: {
        transform: 'translateY(-200%)'
    }
}));

interface IMenuSearchProps {
    open: boolean;
    fullScreen?: boolean;
    onClose: () => void;
}

const Transition = React.forwardRef(
    (
        props: TransitionProps & {
            children: React.ReactElement<any, any>;
        },
        ref: React.Ref<unknown>
    ) => <Slide direction="left" ref={ref} {...props} />
);

export const MenuSearch: React.FC<React.PropsWithChildren<IMenuSearchProps>> = ({ open, onClose }) => {
    const { replace } = useLocalHistory();
    const location = useLocation();
    const searchParams = useQuery('search');

    const appBarRef = React.useRef<HTMLDivElement>(null);
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);

    const [searchInput, setSearchInput] = React.useState(searchParams ?? '');
    const [selectedProduct, setSelectedProduct] = React.useState(
        null as null | IEnrichedProduct | ProductGroup
    );
    const [selectedCategoryId, setSelectedCategoryId] = React.useState<string>();

    const currency = useSelector(
        (state: ApplicationState) => state.settings?.settings?.region?.currencyCode ?? 'GBP'
    );
    const { menu } = useSelector((state: ApplicationState) => state.menu);

    const { t } = useTranslation();
    const { isDesktop } = useResponsive();
    const { groupsResult, categoriesResult, searchMenu } = useMenuSearch();
    const { addModal } = useAddModal();
    const context = useSuggestionsContext();

    React.useEffect(() => {
        searchMenu(searchInput);
    }, [searchInput, searchMenu]);

    React.useEffect(() => {
        function setBlurForInput() {
            if (inputRef && inputRef.current) {
                inputRef.current.blur();
            }
        }

        const dialog = dialogRef?.current;
        if (dialog) {
            dialog.addEventListener('scroll', setBlurForInput);
        }
        window.addEventListener('scroll', setBlurForInput);

        return () => {
            if (dialog) {
                dialog.removeEventListener('scroll', setBlurForInput);
            }
            window.removeEventListener('scroll', setBlurForInput);
        };
    }, [groupsResult, categoriesResult]);

    React.useEffect(() => {
        if (context.suggestedGroupProduct) {
            setSelectedProduct(context.suggestedGroupProduct);
            setSelectedCategoryId(menu?.productIdToCategoryId.get(context.suggestedGroupProduct.id));
        }
    }, [context.suggestedGroupProduct, menu?.productIdToCategoryId]);

    const handleProductDetailsClose = React.useCallback(() => {
        setSelectedCategoryId(undefined);
        setSelectedProduct(null);
        context.setShowMoreSuggestionInfo(false);
    }, [context]);

    const handleChangeSearchInput = React.useCallback(
        (event: any) => {
            const { value } = event.target;
            const searchParams = new URLSearchParams(location.search);
            if (isDefined(value) && !isEmptyString(value)) {
                searchParams.set('search', value);
                replace('', {}, searchParams.toString());
            } else {
                searchParams.delete('search');
                replace('', {}, searchParams.toString());
            }
            setSearchInput(value);
        },
        [location.search, replace]
    );

    const clearInput = React.useCallback(() => {
        setSearchInput('');
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('search');
        replace('', {}, searchParams.toString());
    }, [location.search, replace]);
    const handleProductSelected = React.useCallback(
        (product: IProduct | ProductGroup, categoryId: string) => {
            setSelectedProduct(product);
            setSelectedCategoryId(categoryId);
        },
        []
    );

    const renderLocalisationStrings = React.useCallback(
        (stringKey: any) => (
            <Typography key={stringKey} variant="body1" align="center" className={classes.hint}>
                {reactStringReplace(stringKey, 'searchTerm', item => (
                    <b key={`search-term-${item}`}>{searchInput}</b>
                ))}
            </Typography>
        ),
        [searchInput]
    );

    const renderCategory = React.useCallback(
        (category: ISearchCategory) => (
            <MenuSearchCategory
                key={category.id}
                category={category}
                onProductSelect={handleProductSelected}
            />
        ),
        [handleProductSelected]
    );

    const renderCategoryGroup = React.useCallback(
        (group: ISearchGroup) => (
            <Box key={group.groupId} width="100%" marginBottom={2}>
                <ListItemText
                    primary={group.groupTitle}
                    slotProps={{
                        primary: {
                            className: classes.titleGroup,
                            variant: 'h5'
                        }
                    }}
                />
                <Box paddingLeft={2}>{group.categories.map(renderCategory)}</Box>
            </Box>
        ),
        [renderCategory]
    );

    const handleClose = React.useCallback(() => {
        const searchParams = new URLSearchParams(location.search);
        searchParams.delete('search');
        replace('', {}, searchParams.toString());
        onClose();
    }, [location.search, onClose, replace]);

    if (isDesktop) {
        return null;
    }

    return (
        <Dialog
            fullWidth
            fullScreen
            open={open}
            onClose={handleClose}
            slots={{
                transition: Transition
            }}
            slotProps={{
                paper: { ref: dialogRef }
            }}
        >
            <StyledBox height="100%">
                <div style={{ height: appBarRef.current?.clientHeight || 64 }}></div>
                <div ref={appBarRef} className={classes.appBar}>
                    <IconButton onClick={handleClose} size="large">
                        <ArrowBackIcon className={classes.backButton} />
                    </IconButton>

                    <SearchInput
                        autoFocus
                        inputRef={inputRef}
                        onChange={handleChangeSearchInput}
                        onClear={clearInput}
                        value={searchInput}
                        placeholder={t('SEACRCH_MENU_PLACEHOLDER')}
                        componentClasses={{
                            wrapper: classes.input
                        }}
                    />
                </div>

                {(categoriesResult?.length || groupsResult?.length || selectedProduct) && (
                    <Box className={classes.productsListBox}>
                        {categoriesResult && categoriesResult.map(renderCategory)}
                        {groupsResult && groupsResult.map(renderCategoryGroup)}

                        {selectedProduct && isDefined(selectedCategoryId) && (
                            <MenuProductInfo
                                item={selectedProduct}
                                currency={currency}
                                open={!!selectedProduct}
                                onClose={handleProductDetailsClose}
                                categoryId={selectedCategoryId}
                            />
                        )}
                    </Box>
                )}

                {(groupsResult?.length === 0 || categoriesResult?.length === 0) && (
                    <Box style={{ height: `calc(100% - ${appBarRef.current?.clientHeight}px` }}>
                        <Paper className={clsx(classes.paper, classes.center)}>
                            <Box className={clsx(isDesktop ? '' : classes.messageBox)}>
                                {t('MENU_SEARCH_NO_RESULTS').split('\n').map(renderLocalisationStrings)}
                            </Box>
                        </Paper>
                    </Box>
                )}

                <MenuViewBasketButton />
            </StyledBox>
            {addModal()}
        </Dialog>
    );
};
